import * as tg from "generic-type-guard";
import React, { ComponentType, useCallback, useContext, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";
import { OverviewEngineFactories } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/client-extensions";
import {
	Button,
	ContextMenuItem,
	ReferenceColumn
} from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/overview-model";
import { OverviewEngineApi } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/api";
import { Footer } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/components/footer";
import { OverviewButton } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/components/overview-button";
import { Pagination } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/components/pagination";
import { ContextMenu } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/components/table/sub-components/context-menu";
import { TableBodyCell } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/components/table/sub-components/table-body-cell";
import { DefaultComponentMap } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/configuration/component-map";
import { WidgetMap } from "@com.mgmtp.a12.overviewengine/overviewengine-core/lib/main/view/configuration/widget-map";
import { UaaSelectors } from "@com.mgmtp.a12.uaa/uaa-authentication-client";

import { getSDManifest } from "../../../app/slices/stammdaten/selectors";
import { AssigneeCandidate } from "../../../app/slices/users/types";
import { ActivityIdContext } from "../../../component/ActivityContext";
import { ClientModal, ClientModalProps } from "../../../component/ClientModal";
import { CustomContentBox } from "../../../component/CustomContentBox";
import { CustomOverviewButton } from "../../../component/CustomOverviewButton";
import { CustomPopUpMenu } from "../../../component/CustomPopUpMenu";
import { FooterHeaderTitleDecorator } from "../../../component/FooterHeaderTitleDecorator";
import { OverviewFooterWithCount } from "../../../component/OverviewFooterWithCount";
import { ScopeBasedVisibilityDecorator } from "../../../component/ScopeBasedVisibilityDecorator";
import { RESOURCE_KEYS } from "../../../localization";
import { getApplicationId } from "../../../utilities/applicationKeyUtils";
import { assertNonNullable, assertTrue } from "../../../utilities/assert";
import { hasUpdateDocumentPermission } from "../../../utilities/permissionUtils";
import { isNonNullable } from "../../../utilities/typeGuardUtils";
import {
	isAdmin,
	isUser,
	selectIsAdmin,
	selectHasNeededPermissions,
	selectPermissionsForFv,
	selectStructuredRoles
} from "../../../utilities/user";
import { VISIBILITY_SCOPE } from "../../../utilities/variables/modulfRuntimeAnnotations";
import { GeneralOverviewWidgetsMap } from "../../generalOverviewEngine/GeneralOverviewEngine";
import { exportApplicationData } from "../../processOverview/store/actions";
import { assignStammdatenDocument } from "../../assignment/actions";
import { OverviewAssigneePicker } from "../../assignment/components/AssigneePicker";
import { selectAssigneeCandidatesCreator } from "../../assignment/selectors";

import { createNewDocument, selectRow } from "../actions";
import { STAMMDATEN_PAGE_SIZE } from "../constants";
import { isStammdatenRoot } from "../types";
import { isStammdatenDocumentEditable } from "../utils";

export function StammdatenOverviewEngine(props: OverviewEngineFactories.ViewComponentProps) {
	const { activityId } = props;
	const model = useSelector(ActivitySelectors.activityPropById(activityId, activity => activity.descriptor.model));
	const dispatch = useDispatch();
	const data = useSelector(ActivitySelectors.data(activityId));
	const childActivity = useSelector(ActivitySelectors.childActivityWithInstance(activityId));
	assertNonNullable(model);
	assertTrue(isDocumentListData(data));
	const sdId = getApplicationId(model) ?? "";
	const manifest = useSelector(getSDManifest(sdId));
	const user = useSelector(UaaSelectors.user);
	assertTrue(isUser(user));

	const rowActionState = React.useMemo<OverviewEngineApi.RowActionState>(
		() => ({
			rows: Object.fromEntries(
				data?.documents?.filter(isNonNullable).map(({ id, root }) => {
					let deleteAllowed = true;
					if (isStammdatenRoot(root)) {
						deleteAllowed = isStammdatenDocumentEditable(root);
					}
					return [
						id,
						{
							delete: { hidden: !deleteAllowed }
						}
					];
				}) ?? []
			)
		}),
		[data?.documents]
	);

	const CustomOverviewButtonComponent: React.ComponentType<OverviewButton.Props> = React.useMemo(
		() => CustomOverviewButton(user, manifest),
		[user, manifest]
	);

	const customComponentMap = React.useMemo(() => {
		return {
			...DefaultComponentMap,
			OverviewButton: ScopeBasedVisibilityDecorator(
				CustomOverviewButtonComponent,
				t => t.buttonModel.annotations,
				manifest?.identification.key
			),
			RowAction: ScopeBasedVisibilityDecorator(
				DefaultComponentMap.RowAction,
				t => t.rowActionModel.annotations,
				manifest?.identification.key
			),
			ContextMenu: CustomContextMenuCreator(manifest?.identification.key),
			TableBodyCell: CustomTableBodyCell,
			Pagination: CustomPagination,
			Footer: StammdatenCustomFooter
		};
	}, [manifest?.identification.key, CustomOverviewButtonComponent]);

	const [showModal, setShowModal] = useState(false);

	const clients = useMemo(() => {
		const userClients = selectStructuredRoles(user)
			.filter(
				it =>
					it.key === manifest?.identification.key &&
					it.roles.length > 0 &&
					(it.roles.length > 1 || it.roles[0] !== "betrachter")
			)
			.map(it => it.client ?? "");

		return (
			manifest?.clients
				?.map(it => (it.key === "​" ? "" : (it.key ?? "")))
				.filter(it => isAdmin(user) || userClients.includes(it)) ?? Array.from(userClients.values())
		);
	}, [manifest?.clients, manifest?.identification.key, user]);

	const eventHandlers = useMemo(
		() => ({
			onEventButtonClick: (event: string) => {
				if (event === "add") {
					if (clients.length <= 1 && manifest) {
						const { id, key } = manifest.identification;
						dispatch(
							createNewDocument({
								activityId: activityId,
								childActivityId: childActivity?.id,
								model,
								key,
								client: clients[0],
								appId: id
							})
						);
					} else {
						setShowModal(true);
					}
				}
				if (event === "export" && manifest) {
					dispatch(exportApplicationData({ manifest, anonym: false }));
				}

				if (event === "exportAnonym" && manifest) {
					dispatch(exportApplicationData({ manifest, anonym: true }));
				}
			},
			onRowButtonClick(params: { documentId: string; rowActionModel: Button }) {
				const eventName = params.rowActionModel?.event?.toLowerCase();
				if (eventName === "delete") {
					dispatch(
						ActivityActions.removeData({
							instanceId: params.documentId,
							activityId: activityId
						})
					);
				}
			},
			onRowClick(params: { documentId: string; customEvent?: string }) {
				const splitDocRef = params.documentId.split("/");
				if (splitDocRef.length === 2) {
					const modelId = splitDocRef[0];
					dispatch(selectRow({ activityId, instanceId: params.documentId, modelId }));
				}
			}
		}),
		[manifest, clients, dispatch, activityId, childActivity?.id, model]
	);

	const onCloseModal = useCallback(() => {
		setShowModal(false);
	}, [setShowModal]);
	const onSubmitModal = useCallback<ClientModalProps["onSubmit"]>(
		client => {
			if (manifest) {
				const { id, key } = manifest.identification;
				dispatch(createNewDocument({ activityId: activityId, model, key, client, appId: id }));
				setShowModal(false);
			}
		},
		[activityId, dispatch, model, manifest, setShowModal]
	);

	const emptyConfig = useMemo(() => ({}), []);

	const widgetMap: WidgetMap = useMemo(
		() => ({
			...GeneralOverviewWidgetsMap,
			ContentBox: CustomContentBox,
			PopUpMenu: CustomPopUpMenu(user, manifest),
			Footer: FooterHeaderTitleDecorator(RESOURCE_KEYS.features.stammdaten.footer.overview)
		}),
		[user, manifest]
	);

	return (
		<>
			<ActivityIdContext.Provider value={props.activityId}>
				<OverviewEngineFactories.ViewComponent
					componentMap={customComponentMap}
					configuration={emptyConfig}
					{...props}
					eventHandlers={eventHandlers}
					rowActionState={rowActionState}
					widgetMap={widgetMap}
				/>
			</ActivityIdContext.Provider>
			{showModal && <ClientModal clients={clients} onClose={onCloseModal} onSubmit={onSubmitModal} />}
		</>
	);
}

const isDocumentListData = new tg.IsInterface()
	.withOptionalProperties({
		documents: tg.isArray(new tg.IsInterface().withProperties({ id: tg.isString, root: tg.isObject }).get())
	})
	.get();

function CustomContextMenuCreator(fvKey?: string) {
	return function CustomContextMenu(props: ContextMenu.Props) {
		const permissions = useSelector(selectPermissionsForFv(fvKey));
		const isAdminUser = useSelector(selectIsAdmin);
		const childrenCount = props.contextMenuModel.groups
			.flatMap(it => it.actions)
			.filter(it => !hideAction(it, permissions)).length;
		return <>{(childrenCount !== 0 || isAdminUser) && <DefaultComponentMap.ContextMenu {...props} />}</>;
	};
}

const CustomPagination: React.ComponentType<Pagination.PropsType> = function CustomPagination(props) {
	const pageCount = calculateStammdatenPageCount(props.pageSize, props.pageCount);

	if (pageCount <= 1) {
		return <></>;
	}
	return <DefaultComponentMap.Pagination {...props} pageSize={STAMMDATEN_PAGE_SIZE} pageCount={pageCount} />;
};

/** Visible for testing */
export function calculateStammdatenPageCount(pageSize: number, pageCount: number): number {
	return Math.ceil((pageSize / STAMMDATEN_PAGE_SIZE) * pageCount);
}

function StammdatenCustomFooter(props: Footer.PropsType): React.ReactElement {
	const activityId = useContext(ActivityIdContext);
	const data = useSelector(ActivitySelectors.data(activityId ?? "")) as
		{ totalDocumentsCount?: number; documents?: unknown[] } | undefined;
	const totalCount = data?.totalDocumentsCount ?? data?.documents?.length;

	return <OverviewFooterWithCount {...props} totalCount={totalCount} />;
}

/** Visible for testing */
export function hideAction(contextItem: ContextMenuItem, permissions: string[]): boolean {
	const requiredAccessRight = contextItem.annotations?.find(it => it.name === VISIBILITY_SCOPE)?.value;
	return requiredAccessRight === undefined ? false : !permissions.includes(requiredAccessRight);
}

const ASSIGNEE_COLUMN_ID = "column-assignee-id";
const fullWidthStyle = { width: "100%" };

const CustomTableBodyCell: ComponentType<TableBodyCell.Props> = function CustomTableBodyCell(props) {
	const dispatch = useDispatch();
	const activityId = useContext(ActivityIdContext);
	assertNonNullable(activityId);

	const activityModel = useSelector(
		ActivitySelectors.activityPropById(activityId, activity => activity.descriptor.model)
	);
	const sdId = getApplicationId(activityModel ?? "") ?? "";
	const manifest = useSelector(getSDManifest(sdId));
	const sdKey = manifest?.identification.key ?? "";

	const hasEditPermission = useSelector(selectHasNeededPermissions(hasUpdateDocumentPermission, sdKey || undefined));

	const docRef = props.row.id;
	const candidates = useSelector(selectAssigneeCandidatesCreator(sdKey, "Stammdatenzuweisung"));
	const assigneeRef = useRef<HTMLElement | undefined>(undefined);
	const setAssigneeRef = useCallback((element: HTMLElement | null) => {
		assigneeRef.current = element ?? undefined;
	}, []);
	const onChange = useCallback(
		(assignee: AssigneeCandidate | undefined) =>
			dispatch(assignStammdatenDocument({ activityId, docRef, assignee: assignee ?? null })),
		[dispatch, activityId, docRef]
	);

	const { row, columnModel } = props;

	if (
		columnModel.id === ASSIGNEE_COLUMN_ID &&
		ReferenceColumn.isAssignableFrom(columnModel) &&
		hasEditPermission &&
		isStammdatenRoot(row.root) &&
		isStammdatenDocumentEditable(row.root)
	) {
		const fqn = row.root?.stammdatenDefault?.stammdatenMetadata?.fqn;
		return (
			<div style={fullWidthStyle} onClick={preventCellClick}>
				<OverviewAssigneePicker
					id={`${activityId}-${row.id}-${columnModel.id}`}
					localizedModelText={columnModel.label}
					activityId={activityId}
					elementRef={columnModel.elementRef}
					document={row}
					fvKey={sdKey}
					fqn={fqn}
					candidates={candidates}
					onChange={onChange}
					inputRef={setAssigneeRef}
				/>
			</div>
		);
	}

	return <DefaultComponentMap.TableBodyCell {...props} />;
};

function preventCellClick(event: React.MouseEvent): void {
	event.preventDefault();
	event.stopPropagation();
}
